import React, { useState, useEffect } from 'react';
import { Box, Typography, Container, Grid, CircularProgress, Chip } from '@mui/material';
import PageBanner from '../../components/PageBanner';
import SEO from '../../components/SEO';
import SectionSidebar from '../../components/SectionSidebar';
import SportsIcon from '@mui/icons-material/Sports';
import HikingIcon from '@mui/icons-material/Hiking';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import NearMeIcon from '@mui/icons-material/NearMe';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import LocalDrinkIcon from '@mui/icons-material/LocalDrink';
import PersonPinIcon from '@mui/icons-material/PersonPin';
import CheckroomIcon from '@mui/icons-material/Checkroom';
import CloudIcon from '@mui/icons-material/Cloud';
import GppGoodIcon from '@mui/icons-material/GppGood';
import ExploreIcon from '@mui/icons-material/Explore';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';
import {
  NEARBY_ACTIVITIES_DEFAULT_DESCRIPTION,
  NEARBY_ACTIVITIES_DEFAULTS,
  NEARBY_ACTIVITIES_DEFAULT_TITLE,
  mergeWithNearbyActivityDefaults,
} from '../../data/nearbyActivitiesDefaults';

const GOLD = '#d4af37';

interface ActivityItem {
  id: string;
  name: string;
  type: string;
  duration: string;
  location: string;
  description?: string;
  difficulty?: string;
  imageUrl?: string;
  color?: string;
  mapUrl?: string;
  lat?: number;
  lng?: number;
}

interface ActivitiesContent {
  title: string;
  description: string;
  activities: ActivityItem[];
}

const gradients: [string, string][] = [
  ['#ec4899', '#db2777'],
  ['#22c55e', '#16a34a'],
  ['#3b82f6', '#2563eb'],
  ['#f59e0b', '#d97706'],
  ['#8b5cf6', '#7c3aed'],
  ['#06b6d4', '#0891b2'],
];

const templeLat = 11.4806;
const templeLng = 75.5916;

const getDistanceKm = (lat1: number, lng1: number, lat2: number, lng2: number) => {
  const toRad = (v: number) => (v * Math.PI) / 180;
  const R = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return Math.round(R * c * 10) / 10;
};

const getActivityMapsUrl = (activity: ActivityItem) => {
  if (typeof activity.lat === 'number' && typeof activity.lng === 'number') {
    return `https://www.google.com/maps/dir/?api=1&origin=${templeLat},${templeLng}&destination=${activity.lat},${activity.lng}`;
  }
  if (activity.mapUrl && activity.mapUrl.trim().length > 0) {
    return activity.mapUrl;
  }
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${activity.name}, ${activity.location}`)}`;
};

const difficultyColor: Record<string, string> = {
  Easy: '#22c55e',
  Moderate: '#f59e0b',
  Challenging: '#ef4444',
};

const tips = [
  { text: 'Book adventure activities through certified operators only', icon: VerifiedUserIcon },
  { text: 'Carry sufficient water and energy snacks', icon: LocalDrinkIcon },
  { text: 'Inform someone about your itinerary before trekking', icon: PersonPinIcon },
  { text: 'Wear appropriate sportswear and sturdy footwear', icon: CheckroomIcon },
  { text: 'Check weather forecasts before outdoor activities', icon: CloudIcon },
  { text: 'Follow all safety instructions from tour guides', icon: GppGoodIcon },
];

export default function Activities() {
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState<ActivitiesContent>({
    title: NEARBY_ACTIVITIES_DEFAULT_TITLE,
    description: NEARBY_ACTIVITIES_DEFAULT_DESCRIPTION,
    activities: NEARBY_ACTIVITIES_DEFAULTS.map((activity) => ({ ...activity })),
  });

  useEffect(() => {
    try {
      const unsubscribe = onSnapshot(doc(db, 'nearby_content', 'activities'), (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data() as ActivitiesContent;
          const nextActivities = mergeWithNearbyActivityDefaults(
            Array.isArray(data.activities) ? data.activities : []
          );
          setContent({
            title: data.title || NEARBY_ACTIVITIES_DEFAULT_TITLE,
            description: data.description || NEARBY_ACTIVITIES_DEFAULT_DESCRIPTION,
            activities: nextActivities.map((activity) => ({ ...activity })),
          });
        } else {
          setContent({
            title: NEARBY_ACTIVITIES_DEFAULT_TITLE,
            description: NEARBY_ACTIVITIES_DEFAULT_DESCRIPTION,
            activities: NEARBY_ACTIVITIES_DEFAULTS.map((activity) => ({ ...activity })),
          });
        }
        setLoading(false);
      });
      return () => unsubscribe();
    } catch (error) {
      console.error('Error loading activities:', error);
      setLoading(false);
    }
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress sx={{ color: GOLD }} />
      </Box>
    );
  }

  const items = content.activities.map((a, i) => {
    const distanceKm =
      typeof a.lat === 'number' && typeof a.lng === 'number'
        ? getDistanceKm(templeLat, templeLng, a.lat, a.lng)
        : null;
    return {
      ...a,
      gradient: gradients[i % gradients.length],
      mapsUrl: getActivityMapsUrl(a),
      distanceKm,
    };
  });

  return (
    <Box sx={{ bgcolor: '#f5f0fa', minHeight: '100vh' }}>
      <SEO title="Activities & Experiences Near Kottayil Temple" description="Activities and experiences near Kottayil Temple (Kotta-Kovilakam Kshethram) — trekking, cultural events, local experiences in Kozhikode, Kerala." path="/nearby/activities" keywords="activities near Kottayil temple, things to do Kozhikode, experiences near temple, Kerala activities, trekking Kozhikode, കോഴിക്കോട് ടൂറിസം, activities near Muchukunnu" />
      <PageBanner title="Activities & Experiences" />
      <Container maxWidth="lg" sx={{ mt: { xs: -4, md: -6 }, position: 'relative', zIndex: 2, pb: 10 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={8} lg={9}>
            <Box
              sx={{
                borderRadius: '30px',
                overflow: 'hidden',
                mb: 4,
                boxShadow: '0 22px 64px rgba(74, 23, 92, 0.22)',
                background: 'linear-gradient(145deg, #5b1b67 0%, #812c87 48%, #c2418d 100%)',
                position: 'relative',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  opacity: 0.13,
                  background:
                    'repeating-linear-gradient(120deg, rgba(255,255,255,0.18) 0px, rgba(255,255,255,0.18) 2px, transparent 2px, transparent 14px)',
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  top: -56,
                  right: -56,
                  width: 240,
                  height: 240,
                  borderRadius: '50%',
                  background: 'radial-gradient(circle, rgba(255,255,255,0.16) 0%, transparent 70%)',
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  bottom: -48,
                  left: -48,
                  width: 190,
                  height: 190,
                  borderRadius: '50%',
                  background: 'radial-gradient(circle, rgba(255,255,255,0.12) 0%, transparent 72%)',
                }}
              />

              <Box sx={{ px: { xs: 3, sm: 4 }, py: { xs: 4, sm: 5 }, position: 'relative', zIndex: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, mb: 2.5, flexWrap: 'wrap' }}>
                  <Box
                    sx={{
                      width: 66,
                      height: 66,
                      borderRadius: '20px',
                      background: 'rgba(255,255,255,0.2)',
                      backdropFilter: 'blur(10px)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '2px solid rgba(255,255,255,0.25)',
                      boxShadow: '0 12px 30px rgba(58,12,66,0.3)',
                    }}
                  >
                    <HikingIcon sx={{ fontSize: 32, color: '#fff' }} />
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        fontWeight: 900,
                        fontSize: { xs: '1.6rem', sm: '2rem' },
                        color: '#fff',
                        lineHeight: 1.15,
                        mb: 0.5,
                        textShadow: '0 2px 12px rgba(0,0,0,0.22)',
                      }}
                    >
                      Adventure Activities Nearby
                    </Typography>
                    <Typography sx={{ fontSize: { xs: '0.82rem', sm: '0.9rem' }, color: 'rgba(255,255,255,0.68)', fontWeight: 500 }}>
                      Outdoor fun and guided experiences around Muchukunnu
                    </Typography>
                  </Box>
                </Box>
                <Typography sx={{ fontSize: { xs: '0.88rem', sm: '0.95rem' }, color: 'rgba(255,255,255,0.58)', lineHeight: 1.9, maxWidth: 560, mb: 2.5 }}>
                  From trekking and nature walks to local adventure experiences, choose activities based on your pace,
                  duration, and comfort level.
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2.2 }}>
                  <Chip icon={<ExploreIcon sx={{ fontSize: '16px !important', color: '#b42362 !important' }} />} label={`${items.length} Activity${items.length !== 1 ? 'ies' : ''}`} sx={{ bgcolor: 'rgba(255,255,255,0.95)', color: '#6b1e73', fontWeight: 700, fontSize: '0.78rem', height: 34, borderRadius: '12px', '& .MuiChip-icon': { ml: '8px' } }} />
                  <Chip icon={<HikingIcon sx={{ fontSize: '16px !important', color: '#f9a8d4 !important' }} />} label="Outdoor & Nature" sx={{ bgcolor: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.92)', fontWeight: 700, fontSize: '0.78rem', height: 34, borderRadius: '12px', border: '1px solid rgba(255,255,255,0.18)', '& .MuiChip-icon': { ml: '8px' } }} />
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0,1fr))' }, gap: 1.3 }}>
                  {[
                    { label: 'Activity Directory', value: `${items.length}`, icon: SportsIcon },
                    { label: 'Difficulty Range', value: 'Easy to Challenging', icon: FitnessCenterIcon },
                    { label: 'Trip Duration', value: 'Short to Half-Day', icon: AccessTimeIcon },
                  ].map((metric) => {
                    const MIcon = metric.icon;
                    return (
                      <Box key={metric.label} sx={{ borderRadius: '14px', p: 1.4, bgcolor: 'rgba(255,255,255,0.13)', border: '1px solid rgba(255,255,255,0.16)' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.1 }}>
                          <Box sx={{ width: 28, height: 28, borderRadius: '9px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <MIcon sx={{ fontSize: 16, color: '#fff' }} />
                          </Box>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ color: '#fff', fontSize: '0.8rem', fontWeight: 700, lineHeight: 1.2 }}>{metric.value}</Typography>
                            <Typography sx={{ color: 'rgba(255,255,255,0.74)', fontSize: '0.7rem', lineHeight: 1.2 }}>{metric.label}</Typography>
                          </Box>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </Box>

            {items.length === 0 ? (
              <Box sx={{ bgcolor: '#fff', borderRadius: '24px', p: 6, textAlign: 'center', border: '1px solid rgba(0,0,0,0.06)', boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }}>
                <SportsIcon sx={{ fontSize: 56, color: '#e0e0e0', mb: 2 }} />
                <Typography sx={{ fontSize: { xs: '0.88rem', md: '1rem' }, color: '#bbb', fontWeight: 600 }}>Activity information coming soon.</Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0,1fr))' }, gap: 2.5, mb: 4 }}>
                {items.map((a) => {
                  const [gStart, gEnd] = a.gradient;
                  const dColor = difficultyColor[a.difficulty || ''] || '#64748b';
                  return (
                    <Box key={a.id || a.name} sx={{
                      bgcolor: '#fff', borderRadius: '24px', overflow: 'hidden',
                      border: '1px solid rgba(95,36,109,0.12)', boxShadow: '0 4px 24px rgba(83,30,94,0.1)',
                      transition: 'all 400ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                      '&:hover': { transform: 'translateY(-6px)', boxShadow: `0 20px 60px rgba(83,30,94,0.2), 0 0 0 1px ${gStart}35` },
                      '&:hover .activity-card-img': { transform: 'scale(1.06)' },
                    }}>
                      <Box sx={{ position: 'relative' }}>
                        {a.imageUrl ? (
                          <Box sx={{ width: '100%', height: { xs: 200, sm: 240 }, position: 'relative', overflow: 'hidden' }}>
                            <Box
                              component="img"
                              src={a.imageUrl}
                              alt={a.name}
                              className="activity-card-img"
                              sx={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                transition: 'transform 600ms ease',
                              }}
                            />
                            <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.58) 0%, rgba(0,0,0,0.15) 50%, rgba(0,0,0,0.1) 100%)' }} />
                            <Box sx={{ position: 'absolute', bottom: 14, left: 14, right: 14, display: 'flex', alignItems: 'center', gap: 1.2 }}>
                              <Box
                                sx={{
                                  width: 42,
                                  height: 42,
                                  borderRadius: '13px',
                                  background: `linear-gradient(135deg, ${gStart}, ${gEnd})`,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  boxShadow: `0 6px 20px ${gStart}55`,
                                  flexShrink: 0,
                                }}
                              >
                                <HikingIcon sx={{ fontSize: 22, color: '#fff' }} />
                              </Box>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.02rem', sm: '1.12rem' }, color: '#fff', lineHeight: 1.2, textShadow: '0 2px 10px rgba(0,0,0,0.28)' }}>
                                  {a.name}
                                </Typography>
                                <Typography sx={{ fontSize: '0.76rem', color: 'rgba(255,255,255,0.84)', fontWeight: 500 }}>
                                  {a.location}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        ) : (
                          <Box sx={{
                            background: `linear-gradient(135deg, ${gStart}, ${gEnd})`,
                            px: { xs: 2.5, sm: 3 }, py: { xs: 2, sm: 2.5 },
                            position: 'relative', overflow: 'hidden',
                          }}>
                            <Box sx={{ position: 'absolute', top: -15, right: -15, width: 80, height: 80, borderRadius: '50%', background: 'rgba(255,255,255,0.1)' }} />
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, position: 'relative', zIndex: 2 }}>
                              <Box sx={{ width: 46, height: 46, borderRadius: '14px', background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(10px)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(255,255,255,0.25)' }}>
                                <HikingIcon sx={{ fontSize: 24, color: '#fff' }} />
                              </Box>
                              <Box sx={{ flex: 1 }}>
                                <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.05rem', sm: '1.15rem' }, color: '#fff', lineHeight: 1.2 }}>
                                  {a.name}
                                </Typography>
                                <Typography sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.75)', fontWeight: 500 }}>
                                  {a.location}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        )}
                      </Box>

                      <Box sx={{ px: { xs: 2.5, sm: 3 }, py: 2.5, bgcolor: '#fdf9ff' }}>
                        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                          {a.distanceKm !== null && (
                            <Chip
                              component="a"
                              href={a.mapsUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              clickable
                              icon={<NearMeIcon sx={{ fontSize: '15px !important', color: `${gStart} !important` }} />}
                              label={`${a.distanceKm} km`}
                              size="small"
                              sx={{
                                bgcolor: `${gStart}10`,
                                color: gStart,
                                fontWeight: 700,
                                fontSize: '0.75rem',
                                height: 30,
                                borderRadius: '10px',
                                border: `1px solid ${gStart}20`,
                                textDecoration: 'none',
                                cursor: 'pointer',
                                '& .MuiChip-icon': { ml: '6px' },
                                '&:hover': { bgcolor: `${gStart}20` },
                              }}
                            />
                          )}
                          <Chip
                            icon={<ExploreIcon sx={{ fontSize: '14px !important', color: '#64748b !important' }} />}
                            label={a.type}
                            size="small"
                            sx={{
                              bgcolor: 'rgba(100,116,139,0.08)',
                              color: '#64748b',
                              fontWeight: 600,
                              fontSize: '0.75rem',
                              height: 30,
                              borderRadius: '10px',
                              border: '1px solid rgba(100,116,139,0.12)',
                              '& .MuiChip-icon': { ml: '6px' },
                            }}
                          />
                          {a.duration && (
                            <Chip icon={<AccessTimeIcon sx={{ fontSize: '14px !important', color: '#64748b !important' }} />} label={a.duration} size="small" sx={{ bgcolor: 'rgba(100,116,139,0.08)', color: '#64748b', fontWeight: 600, fontSize: '0.75rem', height: 30, borderRadius: '10px', border: '1px solid rgba(100,116,139,0.12)', '& .MuiChip-icon': { ml: '6px' } }} />
                          )}
                          {a.difficulty && (
                            <Chip icon={<FitnessCenterIcon sx={{ fontSize: '14px !important', color: `${dColor} !important` }} />} label={a.difficulty} size="small" sx={{ bgcolor: `${dColor}10`, color: dColor, fontWeight: 700, fontSize: '0.75rem', height: 30, borderRadius: '10px', border: `1px solid ${dColor}20`, '& .MuiChip-icon': { ml: '6px' } }} />
                          )}
                        </Box>
                        {a.description && (
                          <Typography sx={{ fontSize: '0.86rem', color: '#6f5a7b', lineHeight: 1.8, letterSpacing: '0.01em', minHeight: 74 }}>
                            {a.description}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}

            <Box sx={{
              background: 'linear-gradient(145deg, #2d1632 0%, #46204f 45%, #1f1024 100%)',
              borderRadius: '24px', p: { xs: 3, sm: 3.5 }, position: 'relative', overflow: 'hidden',
              border: '1px solid rgba(255,255,255,0.06)', boxShadow: '0 8px 32px rgba(0,0,0,0.16)',
            }}>
              <Box sx={{ position: 'absolute', inset: 0, opacity: 0.06, backgroundImage: 'linear-gradient(115deg, rgba(255,255,255,0.15) 0 2px, transparent 2px 12px)' }} />
              <Box sx={{ position: 'absolute', top: -30, right: -30, width: 120, height: 120, borderRadius: '50%', background: `radial-gradient(circle, ${GOLD}18 0%, transparent 70%)` }} />
              <Box sx={{ position: 'relative', zIndex: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                  <Box sx={{ width: 40, height: 40, borderRadius: '12px', background: `linear-gradient(135deg, ${GOLD}, #b8941f)`, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 6px 20px ${GOLD}30` }}>
                    <TipsAndUpdatesIcon sx={{ fontSize: 20, color: '#fff' }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 800, fontSize: { xs: '0.88rem', md: '1rem' }, color: '#fff', lineHeight: 1.2 }}>Adventure Tips</Typography>
                    <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.35)', fontWeight: 500 }}>Stay safe, have fun</Typography>
                  </Box>
                </Box>
                <Grid container spacing={1.5}>
                  {tips.map((t, i) => {
                    const TIcon = t.icon;
                    return (
                      <Grid item xs={12} sm={6} key={i}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1, px: 1.5, borderRadius: '10px', transition: 'all 250ms', border: '1px solid rgba(255,255,255,0.06)', bgcolor: 'rgba(255,255,255,0.02)', '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' } }}>
                          <Box sx={{ width: 24, height: 24, borderRadius: '7px', bgcolor: `${GOLD}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <TIcon sx={{ fontSize: 13, color: GOLD }} />
                          </Box>
                          <Typography sx={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.75)', fontWeight: 500, lineHeight: 1.6 }}>{t.text}</Typography>
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={4} lg={3}>
            <SectionSidebar sectionKey="nearby" activePath="/nearby/activities" />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
